// Exact console contract from the released offer worker. A stored offer marker is
// intent only; it is not proof that a queue job exists or can still run.
const JOB_LABELS = {
    queued: "Queued", running: "Running", failed: "Retry pending", retryable: "Retry pending",
    dead: "Needs attention", succeeded: "Finished", skipped: "Skipped",
};
export const jobLabel = status => JOB_LABELS[status] || "Status unavailable";
export const currentSourceJobs = ingestion => {
    const seen = new Set();
    // The SQL index orders jobs newest-first. Keep the latest per source resource.
    return (ingestion?.jobs || []).filter((job, index) => {
        const key = job.resourceKey ? `${job.connectorKind}:${job.resourceKey}` : `unknown:${index}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};
export async function pageJobsForOffers(index) {
    const jobs = (index.ingestion?.jobs || []).filter(job => job.connectorKind === "public_website" && typeof job.resourceKey === "string" && job.resourceKey.startsWith("page:"));
    if (!jobs.length || !globalThis.crypto?.subtle) return {};
    const hashes = new Map();
    const entries = await Promise.all((index.offers || []).map(async offer => {
        if (!offer.pageUrl || offer.pageDiscovery || offer.pageDiscoveryStatus === "observed") return [offer.offerKey, null];
        try {
            // Hash the exact saved URL bytes, just as PostgreSQL digest(url,'sha256').
            // Do not normalize host case, slash, query order or encoded characters.
            if (!hashes.has(offer.pageUrl)) hashes.set(offer.pageUrl, globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(offer.pageUrl)).then(buffer => `page:${Array.from(new Uint8Array(buffer), byte => byte.toString(16).padStart(2, "0")).join("")}`));
            const resourceKey = await hashes.get(offer.pageUrl);
            return [offer.offerKey, jobs.find(job => job.resourceKey === resourceKey) || null];
        } catch { return [offer.offerKey, null]; }
    }));
    return Object.fromEntries(entries);
}
export function discoveryState(offer, ingestion, pageJob) {
    if (offer.pageDiscovery || offer.pageDiscoveryStatus === "observed") return {
        label: "page observed", description: "Saved page evidence is available. Detected terms remain proposals until confirmed.",
    };
    if (ingestion?.enabled === false) return {
        label: "page scanning disabled", description: "Source ingestion is disabled, so page discovery cannot run. No saved page evidence is available.",
    };
    // The index has page-resource identity, but no offer-specific scan generation.
    // Describe only the latest job for that exact page; never claim it is this offer's job.
    const prefix = "Latest reported scan for this exact page URL: ";
    const states = {
        queued: ["page scan queued", "queued. Detected details appear only after matching page evidence is saved."],
        running: ["page scan running", "running. Matching page evidence is not available yet."],
        failed: ["page scan retry pending", "the last attempt failed and a retry is pending."],
        retryable: ["page scan retry pending", "a retry is pending."],
        dead: ["page scan needs attention", "stopped after unsuccessful attempts. No matching page evidence is available; review the source connection."],
        succeeded: ["page evidence unavailable", "finished, but matching evidence for this offer is not available."],
        skipped: ["page scan skipped", "skipped. No matching page evidence is available."],
    };
    if (ingestion?.enabled === true && pageJob && states[pageJob.status]) {
        const [label, description] = states[pageJob.status];
        return { label, description: prefix + description };
    }
    return {
        label: "page scan status unknown",
        description: "No matching page evidence or current scan status is available. The stored discovery request alone does not confirm that a scan is queued. Recent job history may be incomplete.",
    };
}
export function coverageFacts(job) {
    const coverage = job.coverage || {};
    const fields = [
        ["inventoryComplete", "Inventory", "Complete", "Incomplete"],
        ["persistenceVerified", "Saved records", "Verified", "Not verified"],
        ["metricsComplete", "Metrics", "Complete", "Incomplete"],
        ["analysisComplete", "Analysis", "Complete", "Not complete"],
        ["reconciliationComplete", "Offer matching", "Complete", "Incomplete"],
    ];
    return fields.map(([key, label, yes, no]) => ({ key, label, value: coverage[key] === true ? yes : coverage[key] === false ? no : "Unavailable" }));
}
